import { useState, useEffect } from "react";
import { 
  Search, X, ChevronDown, ChevronUp, MapPin, Filter, 
  SlidersHorizontal, Check, Loader2 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";

// US States for dropdown
const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "Washington D.C." }
];

// Radius options
const RADIUS_OPTIONS = [5, 10, 25, 50, 100, 200];

// Price range presets by type
const PRICE_PRESETS = {
  vehicles: [
    { label: "Under $10K", min: 0, max: 10000 },
    { label: "$10K - $25K", min: 10000, max: 25000 },
    { label: "$25K - $50K", min: 25000, max: 50000 },
    { label: "$50K - $100K", min: 50000, max: 100000 },
    { label: "$100K+", min: 100000, max: null },
  ],
  real_estate: [
    { label: "Under $200K", min: 0, max: 200000 },
    { label: "$200K - $400K", min: 200000, max: 400000 },
    { label: "$400K - $600K", min: 400000, max: 600000 },
    { label: "$600K - $1M", min: 600000, max: 1000000 },
    { label: "$1M+", min: 1000000, max: null },
  ],
  rvs: [
    { label: "Under $25K", min: 0, max: 25000 },
    { label: "$25K - $50K", min: 25000, max: 50000 },
    { label: "$50K - $100K", min: 50000, max: 100000 },
    { label: "$100K - $200K", min: 100000, max: 200000 },
    { label: "$200K+", min: 200000, max: null },
  ],
  furniture: [
    { label: "Under $500", min: 0, max: 500 },
    { label: "$500 - $1K", min: 500, max: 1000 },
    { label: "$1K - $2.5K", min: 1000, max: 2500 },
    { label: "$2.5K - $5K", min: 2500, max: 5000 },
    { label: "$5K+", min: 5000, max: null },
  ],
};

// Collapsible filter section component
function FilterSection({ title, isOpen, onToggle, children, count }) {
  return (
    <div className="border-b border-white/10 py-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-white font-medium text-sm"
      >
        <span className="flex items-center gap-2">
          {title}
          {count > 0 && (
            <span className="px-2 py-0.5 bg-[#7C3AED] text-white text-xs rounded-full">
              {count}
            </span>
          )}
        </span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 space-y-2">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Multi-select dropdown component
function MultiSelect({ options, selected, onChange, placeholder, searchable = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  
  const filteredOptions = searchable && search
    ? options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()))
    : options;
  
  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white hover:border-white/20"
      >
        <span className={selected.length ? "text-white" : "text-[#64748B]"}>
          {selected.length ? `${selected.length} selected` : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[#0F172A] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-white/10">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="h-8 bg-white/5 border-white/10 text-white text-sm"
              />
            </div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => toggleOption(option)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10"
                >
                  <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                    selected.includes(option) ? "bg-[#7C3AED] border-[#7C3AED]" : "border-white/30"
                  }`}>
                    {selected.includes(option) && <Check className="w-3 h-3" />}
                  </div>
                  {option}
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-[#64748B] text-center">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Range input component
function RangeInput({ label, minValue, maxValue, onMinChange, onMaxChange, prefix = "", suffix = "" }) {
  return (
    <div className="space-y-2">
      {label && <label className="text-xs text-[#94A3B8]">{label}</label>}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">{prefix}</span>}
          <Input
            type="number"
            value={minValue || ""}
            onChange={(e) => onMinChange(e.target.value ? Number(e.target.value) : null)}
            placeholder="Min"
            className={`h-9 bg-white/5 border-white/10 text-white text-sm ${prefix ? "pl-7" : ""}`}
          />
        </div>
        <span className="text-[#64748B]">-</span>
        <div className="flex-1 relative">
          {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">{prefix}</span>}
          <Input
            type="number"
            value={maxValue || ""}
            onChange={(e) => onMaxChange(e.target.value ? Number(e.target.value) : null)}
            placeholder="Max"
            className={`h-9 bg-white/5 border-white/10 text-white text-sm ${prefix ? "pl-7" : ""}`}
          />
          {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">{suffix}</span>}
        </div>
      </div>
    </div>
  );
}

// Main Filters Component
export function MarketplaceFilters({ 
  listingType, 
  filters, 
  onFiltersChange, 
  facets = {},
  onApply,
  onClear,
  isMobile = false,
  isOpen = true,
  onClose
}) {
  const [openSections, setOpenSections] = useState({
    location: true,
    price: true,
    condition: false,
    vehicle: true,
    realEstate: true,
    rv: true,
    furniture: true,
    more: false,
  });
  
  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const updateFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };
  
  const countActiveFilters = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "" && (Array.isArray(value) ? value.length > 0 : true)) {
        count++;
      }
    });
    return count;
  };
  
  const activeFilterCount = countActiveFilters();
  
  // Filter content based on listing type
  const renderTypeSpecificFilters = () => {
    switch (listingType) {
      case "vehicles":
        return (
          <FilterSection 
            title="Vehicle Details" 
            isOpen={openSections.vehicle} 
            onToggle={() => toggleSection("vehicle")}
            count={[filters.make, filters.model, filters.min_year, filters.max_year, filters.body_style, filters.transmission, filters.fuel_type].filter(Boolean).length}
          >
            {/* Make */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Make</label>
              <MultiSelect
                options={facets.makes || ["Ford", "Chevrolet", "Toyota", "Honda", "BMW", "Mercedes-Benz", "Audi", "Lexus", "Nissan", "Hyundai"]}
                selected={filters.make ? [filters.make] : []}
                onChange={(vals) => updateFilter("make", vals[0] || "")}
                placeholder="Select Make"
              />
            </div>
            
            {/* Model - dependent on make */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Model</label>
              <MultiSelect
                options={facets.models || []}
                selected={filters.model ? [filters.model] : []}
                onChange={(vals) => updateFilter("model", vals[0] || "")}
                placeholder="Select Model"
              />
            </div>
            
            {/* Year Range */}
            <RangeInput
              label="Year"
              minValue={filters.min_year}
              maxValue={filters.max_year}
              onMinChange={(val) => updateFilter("min_year", val)}
              onMaxChange={(val) => updateFilter("max_year", val)}
            />
            
            {/* Mileage Range */}
            <RangeInput
              label="Mileage"
              minValue={filters.min_mileage}
              maxValue={filters.max_mileage}
              onMinChange={(val) => updateFilter("min_mileage", val)}
              onMaxChange={(val) => updateFilter("max_mileage", val)}
              suffix="mi"
            />
            
            {/* Body Style */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Body Style</label>
              <select
                value={filters.body_style || ""}
                onChange={(e) => updateFilter("body_style", e.target.value)}
                className="w-full h-9 bg-white/5 border border-white/10 rounded-lg px-3 text-white text-sm [&>option]:bg-[#0F172A]"
              >
                <option value="">Any Body Style</option>
                {(facets.body_styles || ["Sedan", "SUV", "Truck", "Coupe", "Convertible", "Van", "Wagon", "Hatchback"]).map(style => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </select>
            </div>
            
            {/* Transmission */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Transmission</label>
              <div className="flex gap-2">
                {["Automatic", "Manual"].map(trans => (
                  <button
                    key={trans}
                    onClick={() => updateFilter("transmission", filters.transmission === trans ? "" : trans)}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                      filters.transmission === trans 
                        ? "bg-[#7C3AED] text-white" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {trans}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Fuel Type */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Fuel Type</label>
              <div className="flex flex-wrap gap-2">
                {(facets.fuel_types || ["Gas", "Diesel", "Hybrid", "Electric"]).map(fuel => (
                  <button
                    key={fuel}
                    onClick={() => updateFilter("fuel_type", filters.fuel_type === fuel ? "" : fuel)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.fuel_type === fuel 
                        ? "bg-[#10B981] text-white" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {fuel}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Drivetrain */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Drivetrain</label>
              <div className="flex flex-wrap gap-2">
                {(facets.drivetrains || ["FWD", "RWD", "AWD", "4WD"]).map(dt => (
                  <button
                    key={dt}
                    onClick={() => updateFilter("drivetrain", filters.drivetrain === dt ? "" : dt)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.drivetrain === dt 
                        ? "bg-[#00E5FF] text-black" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {dt}
                  </button>
                ))}
              </div>
            </div>
          </FilterSection>
        );
        
      case "real_estate":
        return (
          <FilterSection 
            title="Property Details" 
            isOpen={openSections.realEstate} 
            onToggle={() => toggleSection("realEstate")}
            count={[filters.property_type, filters.min_beds, filters.min_baths, filters.min_sqft].filter(Boolean).length}
          >
            {/* Property Type */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Property Type</label>
              <select
                value={filters.property_type || ""}
                onChange={(e) => updateFilter("property_type", e.target.value)}
                className="w-full h-9 bg-white/5 border border-white/10 rounded-lg px-3 text-white text-sm [&>option]:bg-[#0F172A]"
              >
                <option value="">Any Type</option>
                {(facets.property_types || ["Single Family", "Condo", "Townhome", "Multi-Family", "Land", "Rental"]).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            {/* Beds */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Bedrooms</label>
              <div className="flex gap-1">
                {["Any", "1+", "2+", "3+", "4+", "5+"].map((beds, idx) => (
                  <button
                    key={beds}
                    onClick={() => updateFilter("min_beds", idx === 0 ? null : idx)}
                    className={`flex-1 py-2 rounded text-xs font-medium transition-colors ${
                      (idx === 0 && !filters.min_beds) || filters.min_beds === idx
                        ? "bg-[#EC4899] text-white" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {beds}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Baths */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Bathrooms</label>
              <div className="flex gap-1">
                {["Any", "1+", "2+", "3+", "4+"].map((baths, idx) => (
                  <button
                    key={baths}
                    onClick={() => updateFilter("min_baths", idx === 0 ? null : idx)}
                    className={`flex-1 py-2 rounded text-xs font-medium transition-colors ${
                      (idx === 0 && !filters.min_baths) || filters.min_baths === idx
                        ? "bg-[#EC4899] text-white" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {baths}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Square Feet */}
            <RangeInput
              label="Square Feet"
              minValue={filters.min_sqft}
              maxValue={filters.max_sqft}
              onMinChange={(val) => updateFilter("min_sqft", val)}
              onMaxChange={(val) => updateFilter("max_sqft", val)}
              suffix="sqft"
            />
            
            {/* Year Built */}
            <RangeInput
              label="Year Built"
              minValue={filters.min_year_built}
              maxValue={filters.max_year_built}
              onMinChange={(val) => updateFilter("min_year_built", val)}
              onMaxChange={(val) => updateFilter("max_year_built", val)}
            />
            
            {/* Status */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Status</label>
              <div className="flex flex-wrap gap-2">
                {["For Sale", "For Rent", "Pending", "Sold"].map(status => (
                  <button
                    key={status}
                    onClick={() => updateFilter("status", filters.status === status.toLowerCase().replace(" ", "_") ? "" : status.toLowerCase().replace(" ", "_"))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.status === status.toLowerCase().replace(" ", "_")
                        ? "bg-[#10B981] text-white" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </FilterSection>
        );
        
      case "rvs":
        return (
          <FilterSection 
            title="RV Details" 
            isOpen={openSections.rv} 
            onToggle={() => toggleSection("rv")}
            count={[filters.rv_type, filters.min_sleeps, filters.min_year, filters.make].filter(Boolean).length}
          >
            {/* RV Type */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">RV Type</label>
              <select
                value={filters.rv_type || ""}
                onChange={(e) => updateFilter("rv_type", e.target.value)}
                className="w-full h-9 bg-white/5 border border-white/10 rounded-lg px-3 text-white text-sm [&>option]:bg-[#0F172A]"
              >
                <option value="">Any Type</option>
                {(facets.rv_types || ["Class A", "Class B", "Class C", "Travel Trailer", "Fifth Wheel", "Toy Hauler", "Camper Van", "Pop-Up Camper"]).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            {/* Make */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Make</label>
              <MultiSelect
                options={facets.makes || ["Winnebago", "Airstream", "Jayco", "Thor", "Keystone", "Forest River", "Grand Design"]}
                selected={filters.make ? [filters.make] : []}
                onChange={(vals) => updateFilter("make", vals[0] || "")}
                placeholder="Select Make"
              />
            </div>
            
            {/* Year Range */}
            <RangeInput
              label="Year"
              minValue={filters.min_year}
              maxValue={filters.max_year}
              onMinChange={(val) => updateFilter("min_year", val)}
              onMaxChange={(val) => updateFilter("max_year", val)}
            />
            
            {/* Sleeps */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Sleeps</label>
              <div className="flex gap-1">
                {["Any", "2+", "4+", "6+", "8+", "10+"].map((sleeps, idx) => (
                  <button
                    key={sleeps}
                    onClick={() => updateFilter("min_sleeps", idx === 0 ? null : idx * 2)}
                    className={`flex-1 py-2 rounded text-xs font-medium transition-colors ${
                      (idx === 0 && !filters.min_sleeps) || filters.min_sleeps === idx * 2
                        ? "bg-[#06B6D4] text-white" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {sleeps}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Length */}
            <RangeInput
              label="Length (ft)"
              minValue={filters.min_length}
              maxValue={filters.max_length}
              onMinChange={(val) => updateFilter("min_length", val)}
              onMaxChange={(val) => updateFilter("max_length", val)}
              suffix="ft"
            />
            
            {/* Slide-outs */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Slide-outs</label>
              <div className="flex gap-1">
                {["Any", "1+", "2+", "3+", "4+"].map((slides, idx) => (
                  <button
                    key={slides}
                    onClick={() => updateFilter("min_slideouts", idx === 0 ? null : idx)}
                    className={`flex-1 py-2 rounded text-xs font-medium transition-colors ${
                      (idx === 0 && !filters.min_slideouts) || filters.min_slideouts === idx
                        ? "bg-[#06B6D4] text-white" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {slides}
                  </button>
                ))}
              </div>
            </div>
          </FilterSection>
        );
        
      case "furniture":
        return (
          <FilterSection 
            title="Furniture Details" 
            isOpen={openSections.furniture} 
            onToggle={() => toggleSection("furniture")}
            count={[filters.category, filters.style, filters.material, filters.room_type].filter(Boolean).length}
          >
            {/* Category */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Category</label>
              <select
                value={filters.category || ""}
                onChange={(e) => updateFilter("category", e.target.value)}
                className="w-full h-9 bg-white/5 border border-white/10 rounded-lg px-3 text-white text-sm [&>option]:bg-[#0F172A]"
              >
                <option value="">Any Category</option>
                {(facets.categories || ["Sofa", "Sectional", "Bed", "Dining Table", "Desk", "Chair", "Dresser", "Bookshelf"]).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            {/* Room Type */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Room</label>
              <div className="flex flex-wrap gap-2">
                {(facets.room_types || ["Living Room", "Bedroom", "Dining Room", "Office", "Outdoor"]).map(room => (
                  <button
                    key={room}
                    onClick={() => updateFilter("room_type", filters.room_type === room ? "" : room)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.room_type === room 
                        ? "bg-[#8B5CF6] text-white" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {room}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Style */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Style</label>
              <select
                value={filters.style || ""}
                onChange={(e) => updateFilter("style", e.target.value)}
                className="w-full h-9 bg-white/5 border border-white/10 rounded-lg px-3 text-white text-sm [&>option]:bg-[#0F172A]"
              >
                <option value="">Any Style</option>
                {(facets.styles || ["Modern", "Traditional", "Contemporary", "Mid-Century", "Industrial", "Farmhouse", "Scandinavian"]).map(style => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </select>
            </div>
            
            {/* Material */}
            <div className="space-y-1">
              <label className="text-xs text-[#94A3B8]">Material</label>
              <div className="flex flex-wrap gap-2">
                {(facets.materials || ["Wood", "Metal", "Fabric", "Leather", "Glass", "Rattan"]).map(mat => (
                  <button
                    key={mat}
                    onClick={() => updateFilter("material", filters.material === mat ? "" : mat)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filters.material === mat 
                        ? "bg-[#F59E0B] text-black" 
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                    }`}
                  >
                    {mat}
                  </button>
                ))}
              </div>
            </div>
          </FilterSection>
        );
        
      default:
        return null;
    }
  };
  
  const filterContent = (
    <div className="space-y-0">
      {/* Location Section */}
      <FilterSection 
        title="Location" 
        isOpen={openSections.location} 
        onToggle={() => toggleSection("location")}
        count={[filters.state, filters.city, filters.zip_code].filter(Boolean).length}
      >
        {/* State */}
        <div className="space-y-1">
          <label className="text-xs text-[#94A3B8]">State</label>
          <select
            value={filters.state || ""}
            onChange={(e) => updateFilter("state", e.target.value)}
            className="w-full h-9 bg-white/5 border border-white/10 rounded-lg px-3 text-white text-sm [&>option]:bg-[#0F172A]"
          >
            <option value="">All States</option>
            {US_STATES.map(state => (
              <option key={state.code} value={state.code}>{state.name}</option>
            ))}
          </select>
        </div>
        
        {/* City */}
        <div className="space-y-1">
          <label className="text-xs text-[#94A3B8]">City</label>
          <Input
            value={filters.city || ""}
            onChange={(e) => updateFilter("city", e.target.value)}
            placeholder="Enter city..."
            className="h-9 bg-white/5 border-white/10 text-white text-sm"
          />
        </div>
        
        {/* ZIP + Radius */}
        <div className="space-y-1">
          <label className="text-xs text-[#94A3B8]">ZIP Code</label>
          <div className="flex gap-2">
            <Input
              value={filters.zip_code || ""}
              onChange={(e) => updateFilter("zip_code", e.target.value)}
              placeholder="ZIP"
              className="flex-1 h-9 bg-white/5 border-white/10 text-white text-sm"
            />
            <select
              value={filters.radius || ""}
              onChange={(e) => updateFilter("radius", e.target.value ? Number(e.target.value) : null)}
              className="w-24 h-9 bg-white/5 border border-white/10 rounded-lg px-2 text-white text-sm [&>option]:bg-[#0F172A]"
            >
              <option value="">Radius</option>
              {RADIUS_OPTIONS.map(r => (
                <option key={r} value={r}>{r} mi</option>
              ))}
            </select>
          </div>
        </div>
      </FilterSection>
      
      {/* Price Section */}
      <FilterSection 
        title="Price" 
        isOpen={openSections.price} 
        onToggle={() => toggleSection("price")}
        count={[filters.min_price, filters.max_price].filter(Boolean).length}
      >
        <RangeInput
          minValue={filters.min_price}
          maxValue={filters.max_price}
          onMinChange={(val) => updateFilter("min_price", val)}
          onMaxChange={(val) => updateFilter("max_price", val)}
          prefix="$"
        />
        
        {/* Quick Price Presets */}
        <div className="flex flex-wrap gap-2 mt-2">
          {(PRICE_PRESETS[listingType] || PRICE_PRESETS.vehicles).slice(0, 3).map(preset => (
            <button
              key={preset.label}
              onClick={() => {
                updateFilter("min_price", preset.min);
                updateFilter("max_price", preset.max);
              }}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                filters.min_price === preset.min && filters.max_price === preset.max
                  ? "bg-[#7C3AED] text-white"
                  : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </FilterSection>
      
      {/* Condition Section */}
      <FilterSection 
        title="Condition" 
        isOpen={openSections.condition} 
        onToggle={() => toggleSection("condition")}
        count={filters.condition ? 1 : 0}
      >
        <div className="flex gap-2">
          {(facets.conditions || ["New", "Used", "Certified"]).map(cond => (
            <button
              key={cond}
              onClick={() => updateFilter("condition", filters.condition === cond.toLowerCase() ? "" : cond.toLowerCase())}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                filters.condition === cond.toLowerCase()
                  ? "bg-[#10B981] text-white" 
                  : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
              }`}
            >
              {cond}
            </button>
          ))}
        </div>
      </FilterSection>
      
      {/* Type-specific filters */}
      {renderTypeSpecificFilters()}
      
      {/* More Options */}
      <FilterSection 
        title="More Options" 
        isOpen={openSections.more} 
        onToggle={() => toggleSection("more")}
        count={[filters.has_photos, filters.has_video, filters.featured].filter(Boolean).length}
      >
        <div className="space-y-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.has_photos || false}
              onChange={(e) => updateFilter("has_photos", e.target.checked || null)}
              className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#7C3AED] focus:ring-[#7C3AED]"
            />
            <span className="text-sm text-white">Has Photos</span>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.has_video || false}
              onChange={(e) => updateFilter("has_video", e.target.checked || null)}
              className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#7C3AED] focus:ring-[#7C3AED]"
            />
            <span className="text-sm text-white">Has Video</span>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.featured || false}
              onChange={(e) => updateFilter("featured", e.target.checked || null)}
              className="w-4 h-4 rounded bg-white/10 border-white/20 text-[#7C3AED] focus:ring-[#7C3AED]"
            />
            <span className="text-sm text-white">Featured Only</span>
          </label>
        </div>
      </FilterSection>
    </div>
  );
  
  // Mobile drawer
  if (isMobile) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/60 z-40"
            />
            
            {/* Drawer */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-[#0F172A] rounded-t-2xl max-h-[85vh] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[#7C3AED]" />
                  <h3 className="text-white font-bold">Filters</h3>
                  {activeFilterCount > 0 && (
                    <span className="px-2 py-0.5 bg-[#7C3AED] text-white text-xs rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(85vh-140px)] p-4">
                {filterContent}
              </div>
              
              {/* Footer Actions */}
              <div className="flex gap-3 p-4 border-t border-white/10 bg-[#0F172A]">
                <Button
                  onClick={onClear}
                  variant="outline"
                  className="flex-1 border-white/10 text-white hover:bg-white/10"
                >
                  Clear All
                </Button>
                <Button
                  onClick={() => { onApply(); onClose(); }}
                  className="flex-1 bg-[#7C3AED] hover:bg-[#7C3AED]/90"
                >
                  Apply Filters
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }
  
  // Desktop sidebar
  return (
    <div className="w-72 flex-shrink-0">
      <div className="sticky top-24 glass-card rounded-xl p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-[#7C3AED]" />
            <h3 className="text-white font-bold">Filters</h3>
          </div>
          {activeFilterCount > 0 && (
            <button 
              onClick={onClear}
              className="text-xs text-[#94A3B8] hover:text-white"
            >
              Clear all
            </button>
          )}
        </div>
        
        {filterContent}
        
        {/* Apply Button (Desktop) */}
        <Button
          onClick={onApply}
          className="w-full bg-[#7C3AED] hover:bg-[#7C3AED]/90"
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
}

export default MarketplaceFilters;
